import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import Visibility from '@mui/icons-material/VisibilityOutlined'
import VisibilityOff from '@mui/icons-material/VisibilityOffOutlined'
import { Button, Input } from '@/design-system'
import {
  authFieldSx,
  authPasswordFieldSx,
  ErrorBanner,
  FieldError,
  FieldLabel,
  FieldLinkRow,
  FieldWrap,
  FormPage,
  FormSub,
  FormTitle,
  FormWrap,
  // GoogleGlyph,
  LegalText,
  // OrDivider,
  PasswordFieldBox,
  PasswordToggle,
  // SsoButton,
  // SsoStack,
  SwapRow,
} from './__styled'
import { validateEmail, validatePhoneNumber } from '@/utils/helper'
import { useLogin, useUpdateOnlineStatus, useLoginCognito } from '@/hooks/api/auth'
import { useAuth } from '@/hooks/common/useAuth'
import { toast } from 'react-hot-toast'

export default function Login() {
  const [user, setUser] = useState('') // Email or Phone number
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [editing, setEditing] = useState(false)

  const login = useLogin(user, password)
  const loginCognito = useLoginCognito(login.data || '')
  const updateOnlineStatus = useUpdateOnlineStatus(1)
  const { login: localLogin } = useAuth()

  useEffect(() => {
    if (login.isSuccess) {
      loginCognito.mutate()
    }
  }, [login.isSuccess, login.data])

  useEffect(() => {
    if (loginCognito.isSuccess) {
      if (loginCognito.data.accountStatus === '0') {
        toast.error('User is inactive. Activate user and retry.')
        return
      } else if (loginCognito.data.errorCat === 4) {
        toast.error('User is deleted. Create new one.')
        return
      }
      updateOnlineStatus.mutate()
      const { requestID, userID, userName } = loginCognito.data
      localLogin({ requestID, userID, userName })
    }
  }, [loginCognito.isSuccess, loginCognito.data])

  const formError = useMemo(() => {
    const error: Record<string, string> = {}
    if (!user.length) {
      error.user = 'Email or Phone Number is required'
    } else if (!validateEmail(user) && !validatePhoneNumber(user)) {
      error.user = 'Enter Valid Phone/Email'
    }
    return error
  }, [user, password])

  const onChangeUser = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUser(e.target.value)
    setEditing(true)
  }

  const onChangePassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value)
  }

  const onSubmitLogin = () => {
    if (formError.user || !password) return
    login.mutate()
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') onSubmitLogin()
  }

  // const soonToast = () => toast('Coming soon', { icon: '✨' })

  const busy = login.isLoading || loginCognito.isLoading

  return (
    <FormPage>
      <FormWrap>
        <FormTitle>Welcome back</FormTitle>
        <FormSub>Sign in to keep working on your albums.</FormSub>

        {/* SSO not wired yet — restore with Google/Apple when ready
        <SsoStack>
          <SsoButton type='button' onClick={soonToast}>
            <GoogleGlyph /> Continue with Google
          </SsoButton>
          <SsoButton type='button' onClick={soonToast}>
            <span style={{ fontSize: 16 }}></span> Continue with Apple
          </SsoButton>
        </SsoStack>

        <OrDivider>OR</OrDivider>
        */}

        <FieldWrap>
          <FieldLabel>Email or phone</FieldLabel>
          <Input
            variant='filled'
            sx={authFieldSx}
            value={user}
            placeholder='elena@example.com'
            onChange={onChangeUser}
            name='email'
            onKeyDown={handleKeyDown}
          />
          {editing && !!formError.user && <FieldError>{formError.user}</FieldError>}
        </FieldWrap>

        <FieldWrap>
          <FieldLabel>Password</FieldLabel>
          <PasswordFieldBox>
            <Input
              variant='filled'
              sx={authPasswordFieldSx}
              value={password}
              placeholder='Your password'
              onChange={onChangePassword}
              type={showPassword ? 'text' : 'password'}
              name='password'
              onKeyDown={handleKeyDown}
            />
            <PasswordToggle
              type='button'
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <VisibilityOff fontSize='small' /> : <Visibility fontSize='small' />}
            </PasswordToggle>
          </PasswordFieldBox>
        </FieldWrap>

        <FieldLinkRow>
          <Link to='/forgot-password'>Forgot password?</Link>
        </FieldLinkRow>

        {!login.isLoading && !!login.error && (
          <ErrorBanner>
            <FieldError sx={{ mt: 0 }}>Incorrect username or password.</FieldError>
          </ErrorBanner>
        )}

        <Button
          variant='spectrum'
          size='lg'
          fullWidth
          disabled={!!formError.user || !password}
          loading={busy}
          onClick={onSubmitLogin}
        >
          Sign in →
        </Button>

        <LegalText>
          By continuing you agree to the{' '}
          <a href='/terms' target='_blank' rel='noopener noreferrer'>Terms</a> and{' '}
          <a href='/privacy' target='_blank' rel='noopener noreferrer'>Privacy Policy</a>. Your
          photos are yours — full stop.
        </LegalText>
        <SwapRow>
          New here? <Link to='/signup'>Create your account free</Link>
        </SwapRow>
      </FormWrap>
    </FormPage>
  )
}
